import React, { useEffect, useState, useCallback } from 'react';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getSellersList } from '../../../actions/GetSellerListAction';
import { getRegionCode } from '../../../utils/get-region-details';
import { fetchSellersListUtil } from '../../../utils';
import { ChartGrid } from '@mobilitystore/charts-interface';
import Dropdown from '../../../msreact-kit/html-components/drop-down';
import Pagination from '../../../msreact-kit/html-components/table/pagination';
import { dropDownData } from '../../../msreact-kit/html-components/table/data';
import CustomCell from '../../../msreact-kit/html-components/custom-cell';
import Images from '../../../assets/img';
import idConstants from '../../../data/idConstants';
import { RegisterCompany } from '@mobilitystore/user-onboarding-interface';
import { getEnvironment, encodeRequestorPayload } from '../../../utils';
import * as Constants from '../../../actions/configuration';
import SnackBar from '../../../components/Shared/Utils/SnackBar';
import './index.scss';

const ActionCellRenderer = ({ data, onEditClick }) => {
  const isDrafted = data?.status === 'Drafted';

  const handleClick = () => {
    if (isDrafted) {
      onEditClick(data?.userData);
    }
  };

  const handleKeyDown = (e) => {
    if (isDrafted && (e.key === 'Enter' || e.key === ' ')) {
      onEditClick(data?.userData);
    }
  };

  return (
    <div className='icons__edit_icon'>
      <img
        src={Images.editIcon}
        alt={i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.EDIT')}
        title={i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.EDIT')}
        style={{
          cursor: isDrafted ? 'pointer' : 'not-allowed',
          width: 20,
          height: 20,
          opacity: isDrafted ? 1 : 0.4
        }}
        onClick={handleClick}
        tabIndex={isDrafted ? 0 : -1}
        role='button'
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

const UserList = ({ onUserCreated }) => {
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [buyerAuthPayload, setBuyerAuthPayload] = useState(null);
  const [snackBarFlag, setSnackBarFlag] = useState(false);
  const [snackBarMsg, setSnackBarMsg] = useState('');
  const [snackBarSeverity, setSnackBarSeverity] = useState('');
  const dispatch = useDispatch();
  const csmTenants = useSelector((state) => state.UserServiceReducer?.csmTenants);

  // Defensive: ensure sellersList is always an array
  const rawSellersList = useSelector(
    (state) => state.UserServiceReducer?.sellersList
  );
  let sellersList = [];
  if (Array.isArray(rawSellersList)) {
    sellersList = rawSellersList;
  } else if (rawSellersList && Array.isArray(rawSellersList.users)) {
    sellersList = rawSellersList.users;
  }
  const clientToken = localStorage.getItem(idConstants.tokens.aaaIdToken);
  // Defensive: ensure regionCode is present
  const regionCode = getRegionCode ? getRegionCode() : '';
  // Defensive: environment
  const environment = getEnvironment
    ? getEnvironment(process.env.REACT_APP_ENVIRONMENT)
    : '';

  // Pagination state
  // Table expects 0-based currentPage, API expects 1-based
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');

  // Common method to fetch sellers list with current parameters
  const fetchSellersList = useCallback(() => {
    fetchSellersListUtil(
      dispatch,
      getSellersList,
      getRegionCode,
      currentPage,
      rowsPerPage,
      statusFilter,
      csmTenants?.tenantId
    );
  }, [dispatch, currentPage, rowsPerPage, statusFilter, csmTenants?.tenantId]);

  useEffect(() => {
    fetchSellersList();
  }, [fetchSellersList]);

  // Function to refresh the sellers list (memoized to prevent infinite loops)
  const refreshList = useCallback(() => {
    fetchSellersList();
  }, [fetchSellersList]);

  // Expose refreshList function to parent component only once on mount
  useEffect(() => {
    if (onUserCreated) {
      onUserCreated(refreshList);
    }
  }, []); // Empty deps intentional - only call on mount

  // Function to handle edit click
  const handleEditClick = (userData) => {
    const requestorName = `${userData?.firstName} ${userData?.lastName}`.trim();

    const requestorObject = {
      requestorId: userData?.userId,
      requestorName: requestorName,
      requestorEmail: userData?.email
    };

    const encodedPayload = encodeRequestorPayload(requestorObject);

    setSelectedUserId(userData?.userId || userData?.id || userData?._id);
    setSelectedUserData(userData);
    setBuyerAuthPayload(encodedPayload);
    setOpenRegisterModal(true);
  };

  // Status dropdown options with display labels
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'drafted', label: 'Drafted' },
    { value: 'pending_approval', label: 'Pending For Approval' },
    { value: 'pending_mcprolemap', label: 'Pending for Role map' },
    { value: 'approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  // Function to map status values to display labels
  const getStatusLabel = (status) => {
    if (!status) return '-';
    const statusOption = statusOptions.find(
      (option) => option.value.toLowerCase() === status.toLowerCase()
    );
    return statusOption ? statusOption.label : status;
  };

  // AG Grid column definitions
  const colDefs = [
    {
      headerName: i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.CUSTOMER_NAME'),
      field: 'customerName',
      cellRenderer: CustomCell,
      flex: 2
    },
    {
      headerName: i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.EMAIL'),
      field: 'email',
      cellRenderer: CustomCell,
      flex: 2
    },
    {
      headerName: i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.COMPANY_NAME'),
      field: 'companyName',
      cellRenderer: CustomCell,
      flex: 2
    },
    {
      headerName: i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.STATUS'),
      field: 'status',
      cellRenderer: CustomCell,
      flex: 1
    },
    {
      headerName: i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.ACTION'),
      field: 'action',
      cellRenderer: ActionCellRenderer,
      cellRendererParams: {
        onEditClick: handleEditClick
      },
      flex: 1
    }
  ];

  // Map sellersList to AG Grid rowData
  const rowData = Array.isArray(sellersList)
    ? sellersList.map((user) => ({
        userId: user.userId || user.id || user._id,
        customerName:
          [user.firstName, user.lastName].filter(Boolean).join(' ') || '-',
        email: user.email || user.emailId || '-',
        companyName: user.companyName || user.company || '-',
        status: getStatusLabel(user.companyStatus),
        action: '', // handled by cellRenderer
        userData: user // Store full user data for edit
      }))
    : [];

  // Pagination data for Pagination component
  const totalCount =
    rawSellersList?.totalCount || rawSellersList?.total || rowData.length;
  const totalPages =
    rawSellersList?.totalPages ||
    (totalCount && rowsPerPage ? Math.ceil(totalCount / rowsPerPage) : 1);
  const paginationData = {
    totalPages: totalPages,
    totalCount: totalCount
  };

  const handleResponse = (response) => {
    // Refresh the sellers list after creating or editing a user
    fetchSellersList();
  };

  return (
    <div className='user-list'>
      <h5>{i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.ONBOARDED_USERS')}</h5>
      <div className='user-list__status-filter'>
        <div className='user-list__status-filter__items'>
          <div className='user-list__status-filter__items__title'>
            <p>{i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.STATUS')}:</p>
          </div>
          <div className='user-list__status-filter__items__drop'>
            <Dropdown
              options={statusOptions}
              handleChange={(event) => {
                const value = event?.value?.value || event?.target?.value;
                if (value) {
                  setStatusFilter(value);
                  setCurrentPage(0); // Reset to first page when status changes
                }
              }}
              value={
                statusOptions.find((option) => option.value === statusFilter)
                  ?.label || ''
              }
              placeHolder='Please select'
              placeHolderDisabled={true}
            />
          </div>
        </div>
      </div>
      <div className='user-list__ag-table-wrapper'>
        <ChartGrid
          className='user-list__ag-table'
          gridProps={{
            rowData,
            columnDefs: colDefs,
            suppressDragLeaveHidesColumns: true,
            filter: 'agTextColumnFilter',
            suppressMovableColumns: true,
            suppressLoadingOverlay: true,
            suppressScrollOnNewData: true
          }}
        />
      </div>
      <div className='user-list__table'>
        <div className='user-list__table__container'>
          <div className='user-list__table__container__items'>
            <div className='user-list__table__container__items__title'>
              <p>{i18next.t('DYNAMIC_FORM.USER_MANAGEMENT.ITEMS_PER_PAGE')}</p>
            </div>
            <div className='user-list__table__container__items__drop'>
              <Dropdown
                {...dropDownData}
                handleChange={(event) => {
                  let value = event?.value?.value;
                  if (!value && event?.target?.value)
                    value = Number(event.target.value);
                  if (value) {
                    setRowsPerPage(value);
                    setCurrentPage(0);
                  }
                }}
                value={rowsPerPage}
              />
            </div>
          </div>
          {paginationData?.totalPages > 1 && (
            <div className='user-list__table__container__pagination'>
              <Pagination
                className='pagination-bar'
                currentPage={currentPage}
                paginationData={paginationData}
                pageSize={rowsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      </div>
      {openRegisterModal && selectedUserId && buyerAuthPayload && (
        <RegisterCompany
          language='en'
          userId={selectedUserId}
          closeHandler={() => {
            setOpenRegisterModal(false);
            handleResponse();
          }}
          clientToken={clientToken}
          environment={environment}
          successHandler={(response) => handleResponse(response)}
          cancelHandler={() => {
            setOpenRegisterModal(false);
            handleResponse();
          }}
          buyerAuthPayload={buyerAuthPayload}
          isOnboardingByCsm={true}
          tenantId={csmTenants?.tenantId}
          errorHandler={(error) => {
            if (error.message) {
              setSnackBarFlag(true);
              setSnackBarMsg(error.message);
              setSnackBarSeverity('error');
            }
          }}
          role={Constants.AAA_CONFIG.BUYER_ROLE}
        />
      )}
      {snackBarFlag && snackBarMsg && (
        <SnackBar
          message={snackBarMsg}
          severity={snackBarSeverity}
          changeState={() => {
            setSnackBarFlag(false);
            setSnackBarMsg('');
            setSnackBarSeverity('');
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          from='codesnippet'
        />
      )}
    </div>
  );
};

export default UserList;
